'use client'

import { useState } from 'react'
import { Switch } from '@headlessui/react'
import ApiClient from '../utils/ApiClient'
import FeedStatusDisplay from '../components/AdminSettings/FeedStatusDisplay'
import PricingMethodModal from '../components/AdminSettings/PricingMethodModal'
import EmailNotificationSettings from '../components/AdminSettings/EmailNotificationSettings'
import ConditionTiersSettings from '../components/AdminSettings/ConditionTiersSettings'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function AdminSettings() {
    const [automaticTimezoneEnabled, setAutomaticTimezoneEnabled] = useState(true)
    const [isClearingInventory, setIsClearingInventory] = useState(false)
    const [isSyncingInventory, setIsSyncingInventory] = useState(false)
    const [showFeedStatus, setShowFeedStatus] = useState(false)
    const [currentJobId, setCurrentJobId] = useState(null)
    const [isProcessComplete, setIsProcessComplete] = useState(false)

    // New state for sync process
    const [showSyncFeedStatus, setShowSyncFeedStatus] = useState(false)
    const [syncJobId, setSyncJobId] = useState(null)
    const [isSyncComplete, setIsSyncComplete] = useState(false)

    // State for the FNSKU to SKU assignment process
    const [isAssigningSkus, setIsAssigningSkus] = useState(false)
    const [showAssignmentStatus, setShowAssignmentStatus] = useState(false)
    const [assignmentJobId, setAssignmentJobId] = useState(null)
    const [isAssignmentComplete, setIsAssignmentComplete] = useState(false)

    // State for pricing method modal
    const [showPricingModal, setShowPricingModal] = useState(false)
    const [previewResponse, setPreviewResponse] = useState(null)

    // State for FBA price update
    const [fbaAsin, setFbaAsin] = useState('')
    const [fbaPrice, setFbaPrice] = useState('')
    const [fbaBusinessPrice, setFbaBusinessPrice] = useState('')
    const [isUpdatingPrice, setIsUpdatingPrice] = useState(false)
    const [priceUpdateResponse, setPriceUpdateResponse] = useState(null)

    // State for bulk FBA price update
    const [isBulkUpdating, setIsBulkUpdating] = useState(false)
    const [bulkUpdateResponse, setBulkUpdateResponse] = useState(null)

    // State for shipment sales report
    const [isRequestingReport, setIsRequestingReport] = useState(false)
    const [isRetrievingReport, setIsRetrievingReport] = useState(false)
    const [reportResponse, setReportResponse] = useState(null)

    // Handle FBA price update submission
    const handleFbaPriceUpdate = () => {
        if (!fbaAsin || !fbaPrice) {
            alert('Please enter both ASIN and price');
            return;
        }

        setIsUpdatingPrice(true);
        setPriceUpdateResponse(null);

        const requestData = {
            asin: fbaAsin.trim(),
            price: parseFloat(fbaPrice),
            ...(fbaBusinessPrice && { businessPrice: parseFloat(fbaBusinessPrice) })
        };

        ApiClient.post('/api/fba/update-price', requestData)
            .then(response => {
                setIsUpdatingPrice(false);
                setPriceUpdateResponse(response.data);
                // Clear form on success
                if (response.data.success) {
                    setFbaAsin('');
                    setFbaPrice('');
                    setFbaBusinessPrice('');
                }
            })
            .catch(error => {
                setIsUpdatingPrice(false);
                setPriceUpdateResponse({
                    error: true,
                    message: error.response?.data?.error || error.response?.data?.message || 'Error updating price'
                });
                console.error('Error updating FBA price:', error);
            });
    };

    // Handle bulk FBA price update
    const handleBulkFbaPriceUpdate = () => {
        if (!window.confirm('This will update prices for all items in the price list that have matching New condition listings. Continue?')) {
            return;
        }

        setIsBulkUpdating(true);
        setBulkUpdateResponse(null);

        ApiClient.post('/api/fba/bulk-update-prices')
            .then(response => {
                setIsBulkUpdating(false);
                setBulkUpdateResponse(response.data);
            })
            .catch(error => {
                setIsBulkUpdating(false);
                setBulkUpdateResponse({
                    error: true,
                    message: error.response?.data?.error || error.response?.data?.message || 'Error updating prices'
                });
                console.error('Error bulk updating FBA prices:', error);
            });
    };

    // Handle shipment sales report request
    const handleRequestShipmentSalesReport = () => {
        setIsRequestingReport(true);
        setReportResponse(null);

        ApiClient.post('/api/reports/request-shipment-sales')
            .then(response => {
                setIsRequestingReport(false);
                setReportResponse(response.data);
            })
            .catch(error => {
                setIsRequestingReport(false);
                setReportResponse({
                    success: false,
                    message: error.response?.data?.message || 'Error requesting report'
                });
                console.error('Error requesting shipment sales report:', error);
            });
    };

    // Handle shipment sales report retrieval
    const handleRetrieveShipmentSalesReport = () => {
        setIsRetrievingReport(true);
        setReportResponse(null);

        ApiClient.post('/api/reports/retrieve-shipment-sales')
            .then(response => {
                setIsRetrievingReport(false);
                setReportResponse(response.data);
            })
            .catch(error => {
                setIsRetrievingReport(false);
                setReportResponse({
                    success: false,
                    message: error.response?.data?.message || 'Error retrieving report'
                });
                console.error('Error retrieving shipment sales report:', error);
            });
    };

    // Handle pricing method submission
    const handlePricingMethodSubmit = (pricingMethod, previewOnly = false, debugMerchantSku = null) => {
        setPreviewResponse(null); // Clear any existing preview
        setIsSyncingInventory(true);
        setShowSyncFeedStatus(true);
        setIsSyncComplete(false);
        
        let apiEndpoint = '/api/adminsettings/syncelocalandamazonsfinventory';
        
        // Determine API endpoint based on pricing method and preview flag
        switch (pricingMethod) {
            case 'percentage-off-map':
                apiEndpoint = previewOnly 
                    ? '/api/adminsettings/test/syncelocalandamazonsfinventory'
                    : '/api/adminsettings/syncelocalandamazonsfinventory';
                break;
            case 'lowest-price-per-condition':
                apiEndpoint = previewOnly 
                    ? '/api/adminsettings/syncelocalandamazonsfinventory/lowestprice/preview'
                    : '/api/adminsettings/syncelocalandamazonsfinventory/lowestprice';
                break;
            case 'debug-test-asin':
                apiEndpoint = '/api/adminsettings/syncelocalandamazonsfinventory/debug';
                break;
            default:
                apiEndpoint = '/api/adminsettings/syncelocalandamazonsfinventory';
        }
        
        // Special handling for debug mode which returns immediate JSON response
        if (pricingMethod === 'debug-test-asin') {
            const requestBody = debugMerchantSku ? { merchantSku: debugMerchantSku } : {};
            ApiClient.post(apiEndpoint, requestBody)
                .then(response => {
                    setIsSyncingInventory(false);
                    setShowSyncFeedStatus(false);
                    setPreviewResponse(response.data);
                    setIsSyncComplete(true);
                })
                .catch(error => {
                    setIsSyncingInventory(false);
                    setShowSyncFeedStatus(false);
                    console.error('Error:', error);
                    alert(error.response?.data?.message || 'Error running debug sync');
                });
        } else {
            // Regular async processing for other methods
            ApiClient.post(apiEndpoint)
                .then(response => {
                    if (response.data.jobId) {
                        setSyncJobId(response.data.jobId);
                    }
                })
                .catch(error => {
                    setIsSyncingInventory(false);
                    setShowSyncFeedStatus(false);
                    console.error('Error:', error);
                    alert(error.response?.data?.message || 'Error starting inventory sync');
                });
        }
    }

    return (
        <div className="mx-auto max-w-7xl">
            <div className="flex flex-col">
                <main className="px-4 py-8 sm:px-6 lg:px-0">
                    <div className="mx-auto max-w-3xl space-y-16 sm:space-y-20">
                        <div>
                            <h2 className="text-base/7 font-semibold text-gray-900">Amazon Integration</h2>
                            <p className="mt-1 text-sm/6 text-gray-500">
                                Manage your Amazon marketplace connections and data.
                            </p>

                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Clear all Seller Fulfilled Inventory From Amazon</dt>
                                    <dd className="mt-1 sm:mt-0 sm:flex-auto">
                                        <div className="space-y-4">
                                            <div className="inline-flex items-center">
                                                <button
                                                    type="button"
                                                    disabled={isClearingInventory || isProcessComplete}
                                                    onClick={() => {
                                                        setIsClearingInventory(true);
                                                        setShowFeedStatus(true);
                                                        setIsProcessComplete(false);
                                                        ApiClient.post('/api/adminsettings/clearsflocalinventorybulk', {})
                                                            .then(response => {
                                                                // Process started successfully
                                                                // Status updates will come via WebSocket
                                                                if (response.data.jobId) {
                                                                    setCurrentJobId(response.data.jobId);
                                                                }
                                                            })
                                                            .catch(error => {
                                                                setIsClearingInventory(false);
                                                                setShowFeedStatus(false);
                                                                console.error('Error:', error);
                                                                alert(error.response?.data?.message || 'Error starting inventory clear process');
                                                            });
                                                    }}
                                                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isClearingInventory && !showFeedStatus ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Starting...
                                                        </>
                                                    ) : isProcessComplete ? 'Inventory Cleared - Process Complete' : 'Clear Merchant Fulfilled Inventory'}
                                                </button>
                                            </div>
                                            
                                            {showFeedStatus && (
                                                <FeedStatusDisplay 
                                                    jobId={currentJobId}
                                                    onComplete={(status) => {
                                                        setIsClearingInventory(false);
                                                        setIsProcessComplete(true);
                                                        // Keep status display visible for review
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </dd>
                                </div>
                            </dl>
                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Sync SF Local Inventory w/ Amazon</dt>
                                    <dd className="mt-1 sm:mt-0 sm:flex-auto">
                                        <div className="space-y-4">
                                            <div className="inline-flex items-center">
                                                <button
                                                    type="button"
                                                    disabled={isSyncingInventory || isSyncComplete}
                                                    onClick={() => {
                                                        setPreviewResponse(null); // Clear any existing preview
                                                        setShowPricingModal(true);
                                                    }}
                                                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isSyncingInventory && !showSyncFeedStatus ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Starting...
                                                        </>
                                                    ) : isSyncComplete ? 'Inventory Synced - Process Complete' : 'Sync Merchant Fulfilled Inventory'}
                                                </button>
                                            </div>
                                            
                                            {/* NEW: Add FeedStatusDisplay for real-time updates */}
                                            {showSyncFeedStatus && (
                                                <FeedStatusDisplay 
                                                    jobId={syncJobId}
                                                    onComplete={(status) => {
                                                        setIsSyncingInventory(false);
                                                        setIsSyncComplete(true);
                                                    }}
                                                />
                                            )}
                                            
                                            {/* JSON Preview Response Display */}
                                            {previewResponse && (
                                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                                    <h4 className="text-sm font-medium text-gray-900 mb-3">Preview Response:</h4>
                                                    <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-x-auto whitespace-pre-wrap">
                                                        {JSON.stringify(previewResponse, null, 2)}
                                                    </pre>
                                                    <button
                                                        type="button"
                                                        onClick={() => setPreviewResponse(null)}
                                                        className="mt-2 text-xs text-indigo-600 hover:text-indigo-500"
                                                    >
                                                        Clear Preview
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </dd>
                                </div>
                            </dl>
                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Assign Merchant Sku to FNSKU</dt>
                                    <dd className="mt-1 sm:mt-0 sm:flex-auto">
                                        <div className="space-y-4">
                                            <div className="inline-flex items-center">
                                                <button
                                                    type="button"
                                                    disabled={isAssigningSkus || isAssignmentComplete}
                                                    onClick={() => {
                                                        if (window.confirm('This will update seller_sku and sku_condition for all FNSKUs in the inventory. Continue?')) {
                                                            setIsAssigningSkus(true);
                                                            setShowAssignmentStatus(true);
                                                            setIsAssignmentComplete(false);
                                                            ApiClient.post('/api/adminsettings/assignskustofnsku')
                                                                .then(response => {
                                                                    if (response.data.jobId) {
                                                                        setAssignmentJobId(response.data.jobId);
                                                                    }
                                                                })
                                                                .catch(error => {
                                                                    setIsAssigningSkus(false);
                                                                    setShowAssignmentStatus(false);
                                                                    console.error('Error:', error);
                                                                    alert(error.response?.data?.message || 'Error starting assignment process');
                                                                });
                                                        }
                                                    }}
                                                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isAssigningSkus && !showAssignmentStatus ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Starting...
                                                        </>
                                                    ) : isAssignmentComplete ? 'Assignment Complete' : 'Assign Merchant Sku to FNSKU'}
                                                </button>
                                            </div>
                                            
                                            {showAssignmentStatus && (
                                                <FeedStatusDisplay 
                                                    jobId={assignmentJobId}
                                                    onComplete={(status) => {
                                                        setIsAssigningSkus(false);
                                                        setIsAssignmentComplete(true);
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </dd>
                                </div>
                            </dl>
                            
                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <EmailNotificationSettings />
                            </dl>

                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <ConditionTiersSettings />
                            </dl>

                            {/* Shipment Sales Report Section */}
                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <div className="py-6">
                                    <dt className="font-medium text-gray-900 mb-4">FBA Shipment Sales Report</dt>
                                    <div className="text-sm text-gray-600 mb-4">
                                        Request and retrieve FBA Fulfillment Customer Shipment Sales Data report (last 90 days).
                                    </div>
                                    <dd className="mt-1">
                                        <div className="space-y-4">
                                            <div className="flex gap-3">
                                                <button
                                                    type="button"
                                                    disabled={isRequestingReport}
                                                    onClick={handleRequestShipmentSalesReport}
                                                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isRequestingReport ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Requesting...
                                                        </>
                                                    ) : 'Request Report'}
                                                </button>

                                                <button
                                                    type="button"
                                                    disabled={isRetrievingReport}
                                                    onClick={handleRetrieveShipmentSalesReport}
                                                    className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isRetrievingReport ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Retrieving...
                                                        </>
                                                    ) : 'Retrieve Report'}
                                                </button>
                                            </div>

                                            {/* Report Response Display */}
                                            {reportResponse && (
                                                <div className={`mt-4 p-4 rounded-lg ${reportResponse.success ? 'bg-green-50' : 'bg-red-50'}`}>
                                                    <div className={`text-sm ${reportResponse.success ? 'text-green-700' : 'text-red-700'}`}>
                                                        {reportResponse.message}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setReportResponse(null)}
                                                        className="mt-2 text-xs text-indigo-600 hover:text-indigo-500"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </dd>
                                </div>
                            </dl>

                            {/* FBA Price Update Section */}
                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <div className="py-6">
                                    <dt className="font-medium text-gray-900 mb-4">Update FBA Inventory Pricing</dt>
                                    <div className="text-sm text-gray-600 mb-4">
                                        Updates pricing for FBA inventory items. The system will look up the seller SKU corresponding to the ASIN.
                                    </div>
                                    <dd className="mt-1">
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                                <div>
                                                    <label htmlFor="fba-asin" className="block text-sm font-medium text-gray-700">
                                                        ASIN
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="fba-asin"
                                                        value={fbaAsin}
                                                        onChange={(e) => setFbaAsin(e.target.value)}
                                                        placeholder="B01M3SOVIC"
                                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="fba-price" className="block text-sm font-medium text-gray-700">
                                                        Regular Price ($)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="fba-price"
                                                        value={fbaPrice}
                                                        onChange={(e) => setFbaPrice(e.target.value)}
                                                        placeholder="29.99"
                                                        step="0.01"
                                                        min="0"
                                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="fba-business-price" className="block text-sm font-medium text-gray-700">
                                                        Business Price ($) <span className="text-gray-500">(optional)</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="fba-business-price"
                                                        value={fbaBusinessPrice}
                                                        onChange={(e) => setFbaBusinessPrice(e.target.value)}
                                                        placeholder="27.99"
                                                        step="0.01"
                                                        min="0"
                                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <button
                                                    type="button"
                                                    disabled={isUpdatingPrice || !fbaAsin || !fbaPrice}
                                                    onClick={handleFbaPriceUpdate}
                                                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isUpdatingPrice ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Updating...
                                                        </>
                                                    ) : 'Update FBA Price'}
                                                </button>
                                            </div>

                                            {/* Price Update Response Display */}
                                            {priceUpdateResponse && (
                                                <div className={`mt-4 p-4 rounded-lg ${priceUpdateResponse.error ? 'bg-red-50' : 'bg-green-50'}`}>
                                                    {priceUpdateResponse.error ? (
                                                        <div className="text-sm text-red-700">
                                                            <strong>Error:</strong> {priceUpdateResponse.message}
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <div className="text-sm font-medium text-green-900 mb-2">
                                                                Price updated successfully!
                                                            </div>
                                                            <div className="text-xs text-green-700">
                                                                <p>ASIN: {priceUpdateResponse.asin}</p>
                                                                <p>SKU: {priceUpdateResponse.sku}</p>
                                                                <p>Price: ${priceUpdateResponse.price}</p>
                                                                {priceUpdateResponse.businessPrice && (
                                                                    <p>Business Price: ${priceUpdateResponse.businessPrice}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setPriceUpdateResponse(null)}
                                                        className="mt-2 text-xs text-indigo-600 hover:text-indigo-500"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </dd>
                                </div>
                            </dl>

                            {/* Bulk FBA Price Update Section */}
                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <div className="py-6">
                                    <dt className="font-medium text-gray-900 mb-4">Bulk Update FBA Prices from Price Lists</dt>
                                    <div className="text-sm text-gray-600 mb-4">
                                        Updates pricing for all FBA items based on MAP prices in the price list table. Only updates items with New condition listings.
                                    </div>
                                    <dd className="mt-1">
                                        <div className="space-y-4">
                                            <div>
                                                <button
                                                    type="button"
                                                    disabled={isBulkUpdating}
                                                    onClick={handleBulkFbaPriceUpdate}
                                                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isBulkUpdating ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Updating Prices...
                                                        </>
                                                    ) : 'Bulk Update FBA Prices'}
                                                </button>
                                            </div>

                                            {/* Bulk Update Response Display */}
                                            {bulkUpdateResponse && (
                                                <div className={`mt-4 p-4 rounded-lg ${bulkUpdateResponse.error ? 'bg-red-50' : 'bg-green-50'}`}>
                                                    {bulkUpdateResponse.error ? (
                                                        <div className="text-sm text-red-700">
                                                            <strong>Error:</strong> {bulkUpdateResponse.message}
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <div className="text-sm font-medium text-green-900 mb-2">
                                                                Bulk price update completed!
                                                            </div>
                                                            <div className="text-xs text-green-700">
                                                                <p>Total Items: {bulkUpdateResponse.totalItems}</p>
                                                                <p>Successful Updates: {bulkUpdateResponse.successCount}</p>
                                                                <p>Failed Updates: {bulkUpdateResponse.errorCount}</p>
                                                                {bulkUpdateResponse.errors && bulkUpdateResponse.errors.length > 0 && (
                                                                    <div className="mt-2">
                                                                        <p className="font-semibold">Errors:</p>
                                                                        <ul className="list-disc list-inside">
                                                                            {bulkUpdateResponse.errors.map((error, index) => (
                                                                                <li key={index}>{error}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setBulkUpdateResponse(null)}
                                                        className="mt-2 text-xs text-indigo-600 hover:text-indigo-500"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <div>
                            <h2 className="text-base/7 font-semibold text-gray-900">Profile</h2>
                            <p className="mt-1 text-sm/6 text-gray-500">
                                This information will be displayed publicly so be careful what you share.
                            </p>

                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Full name</dt>
                                    <dd className="mt-1 sm:mt-0 sm:flex-auto">
                                        <div className="flex justify-between">
                                            <div className="text-gray-900">Tom Cook</div>
                                            <button type="button" className="font-semibold text-indigo-600 hover:text-indigo-500">
                                                Update
                                            </button>
                                        </div>
                                    </dd>
                                </div>
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Email address</dt>
                                    <dd className="mt-1 sm:mt-0 sm:flex-auto">
                                        <div className="flex justify-between">
                                            <div className="text-gray-900">tom.cook@example.com</div>
                                            <button type="button" className="font-semibold text-indigo-600 hover:text-indigo-500">
                                                Update
                                            </button>
                                        </div>
                                    </dd>
                                </div>
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Title</dt>
                                    <dd className="mt-1 sm:mt-0 sm:flex-auto">
                                        <div className="flex justify-between">
                                            <div className="text-gray-900">Human Resources Manager</div>
                                            <button type="button" className="font-semibold text-indigo-600 hover:text-indigo-500">
                                                Update
                                            </button>
                                        </div>
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <div>
                            <h2 className="text-base/7 font-semibold text-gray-900">Integrations</h2>
                            <p className="mt-1 text-sm/6 text-gray-500">Connect applications to your account.</p>

                            <ul role="list" className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <li className="flex justify-between gap-x-6 py-6">
                                    <div className="font-medium text-gray-900">QuickBooks</div>
                                    <button type="button" className="font-semibold text-indigo-600 hover:text-indigo-500">
                                        Update
                                    </button>
                                </li>
                            </ul>

                            <div className="flex border-t border-gray-100 pt-6">
                                <button type="button" className="text-sm/6 font-semibold text-indigo-600 hover:text-indigo-500">
                                    <span aria-hidden="true">+</span> Add another application
                                </button>
                            </div>
                        </div>

                        <div>
                            <h2 className="text-base/7 font-semibold text-gray-900">Language and dates</h2>
                            <p className="mt-1 text-sm/6 text-gray-500">
                                Choose what language and date format to use throughout your account.
                            </p>

                            <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Language</dt>
                                    <dd className="mt-1 sm:mt-0 sm:flex-auto">
                                        <div className="flex justify-between">
                                            <div className="text-gray-900">English</div>
                                            <button type="button" className="font-semibold text-indigo-600 hover:text-indigo-500">
                                                Update
                                            </button>
                                        </div>
                                    </dd>
                                </div>
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Date format</dt>
                                    <dd className="mt-1 sm:mt-0 sm:flex-auto">
                                        <div className="flex justify-between">
                                            <div className="text-gray-900">DD-MM-YYYY</div>
                                            <button type="button" className="font-semibold text-indigo-600 hover:text-indigo-500">
                                                Update
                                            </button>
                                        </div>
                                    </dd>
                                </div>
                                <div className="py-6 sm:flex">
                                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                                        Automatic timezone
                                    </dt>
                                    <dd className="mt-1 flex items-center sm:mt-0 sm:flex-auto">
                                        <Switch
                                            checked={automaticTimezoneEnabled}
                                            onChange={setAutomaticTimezoneEnabled}
                                            className={classNames(
                                                automaticTimezoneEnabled ? 'bg-indigo-600' : 'bg-gray-200',
                                                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                                            )}
                                        >
                                            <span className="sr-only">Use setting</span>
                                            <span
                                                aria-hidden="true"
                                                className={classNames(
                                                    automaticTimezoneEnabled ? 'translate-x-5' : 'translate-x-0',
                                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                )}
                                            />
                                        </Switch>
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </main>
            </div>
            
            {/* Pricing Method Modal */}
            <PricingMethodModal
                open={showPricingModal}
                onClose={() => setShowPricingModal(false)}
                onSubmit={handlePricingMethodSubmit}
            />
        </div>
    )
}